'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationListResponse } from '@/lib/backend-api';
import { useSession } from '@/hooks/use-session';

export const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const;
const WATCHLIST_QUERY_KEY = ['watchlist'] as const;
const WATCHLIST_NOTIFICATIONS_QUERY_KEY = [
  'watchlist',
  'notifications',
] as const;

/** Poll while the tab is visible — cheap, and the bell should feel live. */
const POLL_INTERVAL_MS = 60 * 1000;

async function fetchNotifications(
  category?: string,
  limit?: number,
): Promise<NotificationListResponse> {
  const params = new URLSearchParams();
  if (category) params.set('category', category);
  if (limit) params.set('limit', String(limit));
  const qs = params.size ? `?${params}` : '';
  const res = await fetch(`/api/notifications${qs}`);
  if (!res.ok) throw new Error(`/api/notifications returned ${res.status}`);
  const json = await res.json();
  return json.data as NotificationListResponse;
}

async function markRead(id: number): Promise<void> {
  const res = await fetch(`/api/notifications/${id}`, { method: 'POST' });
  if (!res.ok) throw new Error(`POST mark-read returned ${res.status}`);
}

async function markAllRead(): Promise<void> {
  const res = await fetch('/api/notifications/read-all', { method: 'POST' });
  if (!res.ok) throw new Error(`POST read-all returned ${res.status}`);
}

/**
 * The notification store behind the bell and `/account/notifications`.
 * Disabled for guests. Mutations also invalidate the watchlist queries —
 * both read the same rows, so a read there must not leave a stale badge here
 * (and vice versa).
 */
export function useNotifications(category?: string, limit?: number) {
  const { data: session } = useSession();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } =
    useQuery<NotificationListResponse>({
      queryKey: [
        ...NOTIFICATIONS_QUERY_KEY,
        ...(category ? [category] : []),
        ...(limit ? [limit] : []),
      ],
      queryFn: () => fetchNotifications(category, limit),
      enabled: !!session?.user?.sub,
      staleTime: 30 * 1000,
      refetchInterval: POLL_INTERVAL_MS,
    });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: WATCHLIST_QUERY_KEY });
    queryClient.invalidateQueries({
      queryKey: WATCHLIST_NOTIFICATIONS_QUERY_KEY,
    });
  };

  const markReadMutation = useMutation({
    mutationFn: markRead,
    onSuccess: invalidate,
  });
  const markAllReadMutation = useMutation({
    mutationFn: markAllRead,
    onSuccess: invalidate,
  });

  return {
    items: data?.items ?? [],
    unreadCount: data?.unread_count ?? 0,
    isLoading,
    isError,
    refetch,
    markRead: markReadMutation.mutate,
    markAllRead: markAllReadMutation.mutate,
    isMutating: markReadMutation.isPending || markAllReadMutation.isPending,
  };
}
